import { Check, Copy } from "lucide-react";
import React, { useState } from "react";
import { graphql, useMutation } from "react-relay";
import { SlackManagedByRootly } from "src/components/integrations/SlackManagedByRootly";
import { Button } from "src/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "src/components/ui/dialog";
import RootlyIcon from "src/components/ui/icons/rootly";
import SlackIcon from "src/components/ui/icons/slack";
import TierZeroIcon from "src/components/ui/icons/tierzero_mark";
import { LoadingSpinner } from "src/components/ui/loading-spinner";
import { Switch } from "src/components/ui/switch";
import { Tooltip } from "src/components/ui/tooltip";
import { useMe, usePermissions } from "src/context/MeContext";
import { useToast } from "src/hooks/use-toast";
import { getBotName, isSlackManagedByRootly } from "src/utils/brandTheme";

import type { SlackIntegrationAutoRespondMutation } from "./__generated__/SlackIntegrationAutoRespondMutation.graphql";
import type { SlackIntegrationBotMentionsMutation } from "./__generated__/SlackIntegrationBotMentionsMutation.graphql";
import type { SlackIntegrationPrivateChannelsMutation } from "./__generated__/SlackIntegrationPrivateChannelsMutation.graphql";
import type { SlackIntegrationUninstallMutation } from "./__generated__/SlackIntegrationUninstallMutation.graphql";

const uninstallSlackMutation = graphql`
  mutation SlackIntegrationUninstallMutation {
    uninstallSlack {
      success
      error
    }
  }
`;

const autoRespondMutation = graphql`
  mutation SlackIntegrationAutoRespondMutation($enabled: Boolean!) {
    updateAutoRespondInThreads(enabled: $enabled) {
      success {
        organization {
          id
          autoRespondInThreads
        }
      }
      error {
        message
      }
    }
  }
`;

const privateChannelsMutation = graphql`
  mutation SlackIntegrationPrivateChannelsMutation($enabled: Boolean!) {
    updatePrivateChannelsEnabled(enabled: $enabled) {
      success {
        organization {
          id
          privateChannelsEnabled
        }
      }
      error {
        message
      }
    }
  }
`;

const botMentionsMutation = graphql`
  mutation SlackIntegrationBotMentionsMutation($enabled: Boolean!) {
    updateRespondToBotMentions(enabled: $enabled) {
      success {
        organization {
          id
          respondToBotMentions
        }
      }
      error {
        message
      }
    }
  }
`;

interface SlackIntegrationProps {
  isOpen: boolean;
  onClose: () => void;
  onRefetch?: () => void;
  slackbotInviteLink?: string;
  isConnected?: boolean;
  autoRespondInThreads?: boolean;
  privateChannelsEnabled?: boolean;
  respondToBotMentions?: boolean;
}

export default function SlackIntegration({ isOpen, onClose, onRefetch, slackbotInviteLink, isConnected, autoRespondInThreads, privateChannelsEnabled, respondToBotMentions }: SlackIntegrationProps) {
  const [isUninstalling, setIsUninstalling] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [commitUninstallSlack] = useMutation<SlackIntegrationUninstallMutation>(uninstallSlackMutation);
  const [commitAutoRespond] = useMutation<SlackIntegrationAutoRespondMutation>(autoRespondMutation);
  const [commitPrivateChannels] = useMutation<SlackIntegrationPrivateChannelsMutation>(privateChannelsMutation);
  const [commitBotMentions] = useMutation<SlackIntegrationBotMentionsMutation>(botMentionsMutation);
  const { toast } = useToast();
  const me = useMe();
  const { canWrite } = usePermissions();
  const canModify = canWrite("integrations");
  const brandTheme = me.organization?.brandTheme ?? "TIERZERO";
  const isManagedByRootly = isSlackManagedByRootly(brandTheme);
  const partnerName = isManagedByRootly ? "Rootly" : "TierZero";
  const PartnerIcon = isManagedByRootly ? RootlyIcon : TierZeroIcon;
  const botName = getBotName(brandTheme);

  const handleToggleAutoRespond = (enabled: boolean) => {
    commitAutoRespond({
      variables: { enabled },
      onCompleted: (response) => {
        if (response.updateAutoRespondInThreads?.error) {
          toast({
            title: "Failed to update setting",
            description: response.updateAutoRespondInThreads.error.message,
            variant: "error",
          });
        } else {
          toast({
            title: enabled ? "Auto-detect thread follow-ups enabled" : "Auto-detect thread follow-ups disabled",
            variant: "success",
          });
        }
      },
      onError: (error) => {
        toast({
          title: "Failed to update setting",
          description: error.message,
          variant: "error",
        });
      },
    });
  };

  const handleTogglePrivateChannels = (enabled: boolean) => {
    commitPrivateChannels({
      variables: { enabled },
      onCompleted: (response) => {
        if (response.updatePrivateChannelsEnabled?.error) {
          toast({
            title: "Failed to update setting",
            description: response.updatePrivateChannelsEnabled.error.message,
            variant: "error",
          });
        } else {
          toast({
            title: enabled ? "Private channel access enabled" : "Private channel access disabled",
            variant: "success",
          });
        }
      },
      onError: (error) => {
        toast({
          title: "Failed to update setting",
          description: error.message,
          variant: "error",
        });
      },
    });
  };

  const handleToggleBotMentions = (enabled: boolean) => {
    commitBotMentions({
      variables: { enabled },
      onCompleted: (response) => {
        if (response.updateRespondToBotMentions?.error) {
          toast({
            title: "Failed to update setting",
            description: response.updateRespondToBotMentions.error.message,
            variant: "error",
          });
        } else {
          toast({
            title: enabled ? "Bot @mentions enabled" : "Bot @mentions disabled",
            variant: "success",
          });
        }
      },
      onError: (error) => {
        toast({
          title: "Failed to update setting",
          description: error.message,
          variant: "error",
        });
      },
    });
  };

  const handleCopyLink = async () => {
    if (!slackbotInviteLink) return;

    try {
      await navigator.clipboard.writeText(slackbotInviteLink);
      setIsCopied(true);
      toast({
        title: "Installation link copied to clipboard",
        variant: "success",
      });

      // Reset the copied state after 2 seconds
      setTimeout(() => {
        setIsCopied(false);
      }, 2000);
    } catch {
      toast({
        title: "Failed to copy link",
        description: "Please try again",
        variant: "error",
      });
    }
  };

  const handleUninstall = () => {
    setIsUninstalling(true);

    commitUninstallSlack({
      variables: {},
      onCompleted: (response) => {
        setIsUninstalling(false);
        if (response.uninstallSlack?.success) {
          onClose();
          onRefetch?.();
        } else {
          toast({
            title: "Failed to uninstall Slack integration",
            description: response.uninstallSlack?.error || "An unknown error occurred",
            variant: "error",
          });
        }
      },
      onError: (error) => {
        setIsUninstalling(false);
        toast({
          title: "Failed to uninstall Slack integration",
          description: error.message,
          variant: "error",
        });
      },
    });
  };
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-4xl bg-popover text-foreground border-border">
        <DialogHeader>
          <DialogTitle>Connect Slack</DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Add {partnerName} AI to your Slack workspace.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 pt-2">
          {isConnected ? (
            <div className="space-y-6 py-2">
              <div className="flex items-center gap-3 rounded-lg border border-border bg-card p-4">
                <div className="flex items-center gap-2">
                  <SlackIcon className="h-8 w-8" />
                  <span className="text-lg text-muted-foreground">+</span>
                  <PartnerIcon className="h-8 w-8 p-0.5 rounded-sm bg-white text-blue-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">{partnerName} AI is connected to your Slack workspace</p>
                </div>
                <span className="inline-flex items-center rounded-full bg-green-500/10 px-2.5 py-0.5 text-xs font-medium text-green-400">
                  Connected
                </span>
              </div>

              {!isManagedByRootly && (
                <>
                  {canModify && (
                    <div>
                      <h4 className="text-sm font-medium text-foreground mb-3">Settings</h4>
                      <div className="rounded-lg border border-border bg-card p-4 space-y-4">
                        <div className="flex items-center justify-between gap-4">
                          <div className="space-y-1">
                            <p className="text-sm font-medium text-foreground">Allow access to private channels</p>
                            <p className="text-xs text-muted-foreground">
                              When enabled, {botName} can access private Slack channels it has been invited to for tools, typeahead, and channel queries.
                            </p>
                          </div>
                          <Switch
                            checked={privateChannelsEnabled ?? false}
                            onCheckedChange={handleTogglePrivateChannels}
                          />
                        </div>
                        <div className="flex items-center justify-between gap-4">
                          <div className="space-y-1">
                            <p className="text-sm font-medium text-foreground">Auto-detect thread follow-ups</p>
                            <p className="text-xs text-muted-foreground">
                              {botName} responds to relevant messages in threads it is already participating in without being tagged.
                            </p>
                          </div>
                          <Switch
                            checked={autoRespondInThreads ?? false}
                            onCheckedChange={handleToggleAutoRespond}
                          />
                        </div>
                        <div className="flex items-center justify-between gap-4">
                          <div className="space-y-1">
                            <p className="text-sm font-medium text-foreground">Respond to @mentions from other bots</p>
                            <p className="text-xs text-muted-foreground">
                              {botName} responds when another Slack bot @mentions it. For safety, {botName} stops replying in a thread after 25 consecutive responses with no human message in between.
                            </p>
                          </div>
                          <Switch
                            checked={respondToBotMentions ?? false}
                            onCheckedChange={handleToggleBotMentions}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="pt-2">
                    <Tooltip
                      content={!canModify ? "You don't have permission to disconnect integrations" : null}
                      position="top"
                      asChild
                    >
                      <span className="block w-full">
                        <Button
                          onClick={handleUninstall}
                          disabled={isUninstalling || !canModify}
                          variant="outline"
                          className="w-full border-red-600 text-red-400 hover:bg-red-600/10 hover:text-red-300"
                        >
                          {isUninstalling ? (
                            <>
                              <LoadingSpinner className="w-4 h-4 mr-2" />
                              Uninstalling...
                            </>
                          ) : (
                            "Uninstall Slack Integration"
                          )}
                        </Button>
                      </span>
                    </Tooltip>
                  </div>
                </>
              )}
            </div>
          ) : isManagedByRootly ? (
            <SlackManagedByRootly />
          ) : (
            slackbotInviteLink ? (
              <div className="flex flex-col items-center justify-center py-4">
                <div className="flex items-center justify-center mb-8">
                  <SlackIcon className="h-10 w-10" />
                  <span className="mx-2 text-xl">+</span>
                  <PartnerIcon className="h-10 w-10 p-1 rounded-sm bg-white text-blue-600" />
                </div>

                <div className="w-full max-w-md space-y-6">
                  {canModify ? (
                    <a
                      href={slackbotInviteLink}
                      className="w-full bg-[#24292f] text-white px-4 py-2 rounded-md flex items-center justify-center space-x-2 hover:bg-[#2d3339]"
                    >
                      <SlackIcon className="h-5 w-5" />
                      <span>Add to Slack</span>
                    </a>
                  ) : (
                    <Tooltip
                      content="You don't have permission to connect integrations"
                      position="top"
                      asChild
                    >
                      <span className="block w-full">
                        <button
                          disabled
                          className="w-full bg-[#24292f] text-white px-4 py-2 rounded-md flex items-center justify-center space-x-2 opacity-50 cursor-not-allowed"
                        >
                          <SlackIcon className="h-5 w-5" />
                          <span>Add to Slack</span>
                        </button>
                      </span>
                    </Tooltip>
                  )}

                  <div className="pt-6 border-t border-accent">
                    <p className="text-sm text-muted-foreground text-center mb-3">
                      Not a Slack admin? Share the install link with your admin.
                    </p>
                    <Button
                      onClick={handleCopyLink}
                      variant="outline"
                      className="w-full border-border hover:bg-card"
                    >
                      {isCopied ? (
                        <>
                          <Check className="w-4 h-4 mr-2" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4 mr-2" />
                          Copy Install Link
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-4">
                <div className="flex items-center justify-center mb-4 text-yellow-400">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-8 w-8 mr-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                  <span className="text-lg font-medium">
                    Connection Unavailable
                  </span>
                </div>
                <p className="text-center text-muted-foreground">
                  Slack integration link is currently unavailable.
                  <br />
                  Please contact {partnerName} for assistance.
                </p>
              </div>
            )
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
